// Vector Google Maps markers for the public site: crisp on any pixel density,
// colored with the practitioner palette ($primary2 / $body-color).
export const MARKER_COLORS = {
  default: "#4988A3",
  active: "#212121"
}

// Zone page breakdown groups ($primary2 / $secondary-smooth): office only, home visits only, both (half blue, half green).
export const PRACTICE_MODE_COLORS = {
  offices: "#4988A3",
  home_care: "#64AA00",
  both: ["#4988A3", "#64AA00"]
}

// Ring order; "both" draws two bands (green outside, blue inside) like the split pin.
const PRACTICE_MODE_RING_COLORS = { offices: "#4988A3", both: ["#64AA00", "#4988A3"], home_care: "#64AA00" }
const CLUSTER_RING_RADIUS = 20

const PIN_SIZES = {
  default: { width: 32, height: 42 },
  active: { width: 40, height: 52 }
}

const PIN_PATH = "M16 1C7.716 1 1 7.6 1 15.74 1 26.86 16 41 16 41s15-14.14 15-25.26C31 7.6 24.284 1 16 1z"

function svgDataUrl (svg) {
  return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`
}

// `color` is a CSS color, or [left, right] for a pin split down the middle (office + home visits).
export function pinSvg (color) {
  const split = Array.isArray(color)
    ? `<defs><linearGradient id="pin-fill" x1="0" x2="1" y1="0" y2="0"><stop offset="0.5" stop-color="${color[0]}"/><stop offset="0.5" stop-color="${color[1]}"/></linearGradient></defs>`
    : ""
  const fill = split ? "url(#pin-fill)" : color

  return [
    "<svg xmlns=\"http://www.w3.org/2000/svg\" width=\"32\" height=\"42\" viewBox=\"0 0 32 42\">",
    split,
    `<path d="${PIN_PATH}" fill="${fill}" stroke="#ffffff" stroke-width="2"/>`,
    "<circle cx=\"16\" cy=\"15.5\" r=\"5.5\" fill=\"#ffffff\"/>",
    "</svg>"
  ].join("")
}

export function clusterSvg (color) {
  return [
    "<svg xmlns=\"http://www.w3.org/2000/svg\" width=\"48\" height=\"48\" viewBox=\"0 0 48 48\">",
    `<circle cx="24" cy="24" r="23" fill="${color}" fill-opacity="0.18"/>`,
    `<circle cx="24" cy="24" r="17" fill="${color}" stroke="#ffffff" stroke-width="2"/>`,
    "</svg>"
  ].join("")
}

// Cluster as a ring split by practice mode (counts: { offices, both, home_care }) around a dark center.
export function clusterRingSvg (counts) {
  const total = Object.values(counts).reduce((sum, count) => sum + count, 0)
  const circumference = 2 * Math.PI * CLUSTER_RING_RADIUS
  let offset = 0
  const segments = Object.keys(PRACTICE_MODE_RING_COLORS).filter((mode) => counts[mode] > 0).map((mode) => {
    const length = circumference * counts[mode] / total
    const dash = `stroke-dasharray="${length} ${circumference - length}" stroke-dashoffset="${-offset}" transform="rotate(-90 24 24)"`
    const color = PRACTICE_MODE_RING_COLORS[mode]
    offset += length
    if (!Array.isArray(color)) {
      return `<circle cx="24" cy="24" r="${CLUSTER_RING_RADIUS}" fill="none" stroke="${color}" stroke-width="6" ${dash}/>`
    }

    // Same dash on both bands: on the inner (shorter) circle the angles match because the dash is scaled.
    const innerRadius = CLUSTER_RING_RADIUS - 1.5
    const scale = innerRadius / CLUSTER_RING_RADIUS
    const innerDash = `stroke-dasharray="${length * scale} ${(circumference - length) * scale}" stroke-dashoffset="${-(offset - length) * scale}" transform="rotate(-90 24 24)"`
    return [
      `<circle cx="24" cy="24" r="${CLUSTER_RING_RADIUS}" fill="none" stroke="${color[0]}" stroke-width="6" ${dash}/>`,
      `<circle cx="24" cy="24" r="${innerRadius}" fill="none" stroke="${color[1]}" stroke-width="3" ${innerDash}/>`
    ].join("")
  })

  return [
    "<svg xmlns=\"http://www.w3.org/2000/svg\" width=\"48\" height=\"48\" viewBox=\"0 0 48 48\">",
    "<circle cx=\"24\" cy=\"24\" r=\"24\" fill=\"#ffffff\"/>",
    ...segments,
    "<circle cx=\"24\" cy=\"24\" r=\"16\" fill=\"#3E5161\"/>",
    "</svg>"
  ].join("")
}

// Anchored at the pin tip so the point sits exactly on the address.
// `practiceMode` (offices / home_care / both) colors the pin like the zone page breakdown; active stays dark.
export function pinIcon (maps, { active = false, practiceMode = null } = {}) {
  const variant = active ? "active" : "default"
  const { width, height } = PIN_SIZES[variant]
  const color = (!active && PRACTICE_MODE_COLORS[practiceMode]) || MARKER_COLORS[variant]

  return {
    url: svgDataUrl(pinSvg(color)),
    scaledSize: new maps.Size(width, height),
    anchor: new maps.Point(width / 2, height)
  }
}

// `practiceModes` ({ offices, both, home_care } counts): draws the ring version instead of the plain disc.
export function clusterIcon (maps, count, { practiceModes = null } = {}) {
  const size = count >= 10 ? 52 : 44
  const svg = practiceModes ? clusterRingSvg(practiceModes) : clusterSvg(MARKER_COLORS.default)

  return {
    icon: {
      url: svgDataUrl(svg),
      scaledSize: new maps.Size(size, size),
      anchor: new maps.Point(size / 2, size / 2)
    },
    label: {
      text: String(count),
      color: "#ffffff",
      fontSize: "13px",
      fontWeight: "700"
    }
  }
}
